import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Plus, Pencil, Trash2, X } from 'lucide-react';

import { getBackendUrl } from '../utils/getBackendUrl';
const API = `${getBackendUrl()}/api`;

const COLORS = ['amber', 'blue', 'indigo', 'purple', 'rose', 'emerald', 'red', 'cyan'];
const COLOR_DOT = {
  amber: 'bg-amber-400', blue: 'bg-blue-500', indigo: 'bg-indigo-500', purple: 'bg-purple-500',
  rose: 'bg-rose-400', emerald: 'bg-emerald-500', red: 'bg-red-500', cyan: 'bg-cyan-400',
};

const EMPTY = { label: '', icon: '🏅', color: 'amber', desc: '', ruleType: 'none', threshold: '' };

export const AchievementManager = ({ catalog, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null); // null | {slug?, ...EMPTY}

  const openAdd = () => setForm({ ...EMPTY });
  const openEdit = (b) => setForm({
    slug: b.slug,
    label: b.label,
    icon: b.icon || '🏅',
    color: b.color || 'amber',
    desc: b.desc || '',
    ruleType: b.auto_rule?.type || 'none',
    threshold: b.auto_rule?.threshold ? String(b.auto_rule.threshold) : '',
  });

  const save = async () => {
    if (!form.label.trim()) { toast.error('Give the achievement a name'); return; }
    if (form.ruleType !== 'none' && (!form.threshold || Number(form.threshold) < 1)) {
      toast.error('Enter the number required to earn it automatically'); return;
    }
    const payload = {
      label: form.label,
      icon: form.icon || '🏅',
      color: form.color,
      desc: form.desc,
      auto_rule: form.ruleType === 'none' ? null : { type: form.ruleType, threshold: Number(form.threshold) },
    };
    try {
      if (form.slug) {
        await axios.put(`${API}/badges/catalog/${form.slug}`, payload);
        toast.success('Achievement updated');
      } else {
        await axios.post(`${API}/badges/catalog`, payload);
        toast.success('Achievement added');
      }
      setForm(null);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save achievement');
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.label}"? It will also be removed from everyone who earned it.`)) return;
    try {
      await axios.delete(`${API}/badges/catalog/${b.slug}`);
      toast.success('Achievement deleted');
      onChanged();
    } catch (e) {
      toast.error('Failed to delete achievement');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden" data-testid="achievement-manager-card">
      <button
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between px-4 py-3"
        data-testid="achievement-manager-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-gray-900">
          <Settings className="w-4 h-4 text-amber-500" />
          Manage Achievements ({catalog.length})
        </span>
        <span className="text-xs font-bold text-blue-600">{open ? 'Hide' : 'Open'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          <button
            onClick={openAdd}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-blue-300 text-blue-600 text-sm font-bold hover:bg-blue-50"
            data-testid="add-achievement-btn"
          >
            <Plus className="w-4 h-4" /> Add Achievement
          </button>

          {catalog.map((b) => (
            <div key={b.slug} className="flex items-center gap-2.5 p-2 rounded-lg bg-gray-50 border border-gray-100" data-testid={`manage-badge-${b.slug}`}>
              <span className="text-xl leading-none">{b.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-gray-900 truncate">{b.label}</p>
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${COLOR_DOT[b.color] || 'bg-gray-300'}`} />
                  {b.auto_rule && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0">AUTO</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">{b.desc}</p>
              </div>
              <button onClick={() => openEdit(b)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" data-testid={`edit-badge-${b.slug}`}>
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => remove(b)} className="p-1.5 text-red-400 hover:bg-red-50 rounded" data-testid={`delete-badge-${b.slug}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{form.slug ? 'Edit Achievement' : 'New Achievement'}</h3>
              <button onClick={() => setForm(null)} className="p-1 text-gray-400"><X className="w-5 h-5" /></button>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700">Name</label>
              <input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Concrete King"
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-gray-300 text-sm text-gray-900"
                data-testid="achievement-name-input"
              />
            </div>

            <div className="flex gap-3">
              <div className="w-24">
                <label className="text-xs font-bold text-gray-700">Icon (emoji)</label>
                <input
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-gray-300 text-xl text-center"
                  data-testid="achievement-icon-input"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs font-bold text-gray-700">Colour</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-7 h-7 rounded-full ${COLOR_DOT[c]} ${form.color === c ? 'ring-2 ring-offset-2 ring-gray-800' : ''}`}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700">Description</label>
              <textarea
                value={form.desc}
                onChange={(e) => setForm({ ...form, desc: e.target.value })}
                placeholder="What does the employee have to do to earn it?"
                rows={2}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-gray-300 text-sm text-gray-900"
                data-testid="achievement-desc-input"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700">How is it earned?</label>
              <select
                value={form.ruleType}
                onChange={(e) => setForm({ ...form, ruleType: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-gray-300 text-sm text-gray-900 bg-white"
                data-testid="achievement-rule-select"
              >
                <option value="none">Awarded by admin (manual)</option>
                <option value="hours">Automatic — hours logged reaches…</option>
                <option value="photos">Automatic — photos uploaded reaches…</option>
              </select>
              {form.ruleType !== 'none' && (
                <input
                  type="number"
                  min="1"
                  value={form.threshold}
                  onChange={(e) => setForm({ ...form, threshold: e.target.value })}
                  placeholder={form.ruleType === 'hours' ? 'e.g. 250 hours' : 'e.g. 25 photos'}
                  className="w-full mt-2 px-3 py-2 rounded-lg border-2 border-gray-300 text-sm text-gray-900"
                  data-testid="achievement-threshold-input"
                />
              )}
            </div>

            <div className="flex gap-2 pt-2 border-t">
              <button onClick={() => setForm(null)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-700">Cancel</button>
              <button onClick={save} className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold" data-testid="save-achievement-btn">
                {form.slug ? 'Save Changes' : 'Add Achievement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementManager;
